import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import api, { endpoints } from '../../services/api';
import LoadSheetPDF from '../../components/PDF/LoadSheetPDF';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Alert,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  LocalShipping as PalletIcon,
  Lock as LockIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';

const VueloDetail = () => {
  const { awb } = useParams();
  const [vuelo, setVuelo] = useState(null);
  const [pallets, setPallets] = useState([]);
  const [dangerousGoods, setDangerousGoods] = useState({});
  const [distribucion, setDistribucion] = useState([]);
  const [posicionesLibres, setPosicionesLibres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchVueloDetail();
  }, [awb]);

  const fetchVueloDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${endpoints.vuelos.detalle}?awb=${awb}`);
      const data = response.data.data;

      setVuelo(data.vuelo);
      setPallets(data.pallets || []);
      setDangerousGoods(data.dangerous_goods || {});
      setDistribucion(data.distribucion || []);
      setPosicionesLibres(data.posiciones_libres || []);
    } catch (error) {
      console.error('Error al cargar detalle del vuelo:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!vuelo) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>
          Vuelo no encontrado
        </Typography>
        <Button component={Link} to="/vuelos" startIcon={<BackIcon />}>
          Volver a la lista
        </Button>
      </Box>
    );
  }

  const groupedPallets = {
    topside: pallets.filter((p) => !p.lower || p.lower === null),
    lower: pallets.filter((p) => p.lower === 2),
    bulk: pallets.filter((p) => p.lower === 3),
  };

  const totalKilos = pallets.reduce((sum, p) => sum + (parseFloat(p.pesoTotalP) || 0), 0);
  const totalLbs = Math.round(totalKilos * 2.20462);

  const StatCard = ({ value, label, color }) => (
    <Card sx={{ borderLeft: 4, borderColor: `${color}.main` }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ '@media print': { p: 0 } }}>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                VUELO: {vuelo.awb}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', opacity: 0.9 }}>
                <Typography>
                  Equipo: <strong>{vuelo.equipo_nombre}</strong>
                </Typography>
                <Typography>
                  Destino: <strong>{vuelo.Destino}</strong>
                </Typography>
                <Typography>
                  Fecha: <strong>{new Date(vuelo.fecha).toLocaleDateString()}</strong>
                </Typography>
              </Box>
            </Box>
            <Chip
              icon={<LockIcon />}
              label="CERRADO"
              color="error"
              sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 2.5 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3, '@media print': { display: 'none' } }}>
        <Grid item xs={6} md={3}>
          <StatCard value={pallets.length} label="Pallets" color="primary" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard value={`${totalKilos.toLocaleString()} kg`} label="Peso Total" color="success" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard value={`${totalLbs.toLocaleString()} lbs`} label="En Libras" color="info" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard value={posicionesLibres.length} label="Posiciones Libres" color="secondary" />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ mb: 3, '@media print': { display: 'none' } }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable">
          <Tab label="Load Sheet" />
          <Tab label="Detalles Pallets" />
          <Tab label="Distribución" />
          <Tab label="Posiciones Libres" />
        </Tabs>
      </Box>

      {/* Load Sheet Tab */}
      {activeTab === 0 && (
        <Card>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold">
              LOAD SHEET
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" width={50}></TableCell>
                  <TableCell>PALLET NUMBERS</TableCell>
                  <TableCell align="center" sx={{ bgcolor: 'success.50', color: 'success.dark' }}>
                    KILOS
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: 'info.50', color: 'info.dark' }}>
                    LBS
                  </TableCell>
                  <TableCell align="center">DEST</TableCell>
                  <TableCell>DESCRIPTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* TOPSIDE */}
                <TableRow sx={{ bgcolor: 'grey.200' }}>
                  <TableCell colSpan={6}>
                    <Typography fontWeight="bold">TOPSIDE</Typography>
                  </TableCell>
                </TableRow>
                {groupedPallets.topside.map((pallet, idx) => {
                  const lbs = Math.round((pallet.pesoTotalP || 0) * 2.20462);
                  const hasDG = dangerousGoods[pallet.placa];
                  const details = [];
                  if (pallet.xl && pallet.xl !== 'N/A') details.push(pallet.xl);
                  if (hasDG) {
                    hasDG.forEach((dg) => details.push(`UN: ${dg.un}, Clase: ${dg.clase}`));
                  }
                  if (pallet.notas2) details.push(pallet.notas2);

                  return (
                    <TableRow key={pallet.placa} hover>
                      <TableCell align="center" sx={{ bgcolor: 'grey.100' }}>
                        {idx + 1}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="medium">{pallet.placa}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ bgcolor: 'success.50', fontWeight: 'medium' }}>
                        {(pallet.pesoTotalP || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="center" sx={{ bgcolor: 'info.50', fontWeight: 'medium' }}>
                        {lbs.toLocaleString()}
                      </TableCell>
                      <TableCell align="center">{vuelo.Destino}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {details.join(', ')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ bgcolor: 'warning.light' }}>
                  <TableCell colSpan={2}>
                    <Typography fontWeight="bold">TOPSIDE TOTAL</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {groupedPallets.topside
                      .reduce((sum, p) => sum + (p.pesoTotalP || 0), 0)
                      .toLocaleString()}{' '}
                    Kg
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {Math.round(
                      groupedPallets.topside.reduce((sum, p) => sum + (p.pesoTotalP || 0), 0) * 2.20462
                    ).toLocaleString()}{' '}
                    Lbs
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>

                {/* LOWER DECK */}
                {groupedPallets.lower.length > 0 && (
                  <>
                    <TableRow sx={{ bgcolor: 'grey.200' }}>
                      <TableCell colSpan={6}>
                        <Typography fontWeight="bold">LOWER DECK PALLETS</Typography>
                      </TableCell>
                    </TableRow>
                    {groupedPallets.lower.map((pallet, idx) => {
                      const lbs = Math.round((pallet.pesoTotalP || 0) * 2.20462);
                      return (
                        <TableRow key={pallet.placa} hover>
                          <TableCell align="center" sx={{ bgcolor: 'grey.100' }}>
                            {idx + 1}
                          </TableCell>
                          <TableCell fontWeight="medium">{pallet.placa}</TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'success.50' }}>
                            {(pallet.pesoTotalP || 0).toLocaleString()}
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'info.50' }}>
                            {lbs.toLocaleString()}
                          </TableCell>
                          <TableCell align="center">{vuelo.Destino}</TableCell>
                          <TableCell>{pallet.notas2}</TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                )}

                {/* BULK */}
                {groupedPallets.bulk.length > 0 && (
                  <>
                    <TableRow sx={{ bgcolor: 'grey.200' }}>
                      <TableCell colSpan={6}>
                        <Typography fontWeight="bold">LOOSE BULK BELLY</Typography>
                      </TableCell>
                    </TableRow>
                    {groupedPallets.bulk.map((pallet, idx) => {
                      const lbs = Math.round((pallet.pesoTotalP || 0) * 2.20462);
                      return (
                        <TableRow key={pallet.placa} hover>
                          <TableCell align="center" sx={{ bgcolor: 'grey.100' }}>
                            {idx + 1}
                          </TableCell>
                          <TableCell fontWeight="medium">{pallet.placa}</TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'success.50' }}>
                            {(pallet.pesoTotalP || 0).toLocaleString()}
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'info.50' }}>
                            {lbs.toLocaleString()}
                          </TableCell>
                          <TableCell align="center">{vuelo.Destino}</TableCell>
                          <TableCell>{pallet.notas2}</TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                )}

                {/* GRAND TOTAL */}
                <TableRow sx={{ bgcolor: 'warning.main' }}>
                  <TableCell colSpan={2}>
                    <Typography variant="h6" fontWeight="bold">
                      GRAND TOTAL
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="h6" fontWeight="bold">
                      {totalKilos.toLocaleString()} Kg
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="h6" fontWeight="bold">
                      {totalLbs.toLocaleString()} Lbs
                    </Typography>
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Detalles Pallets Tab */}
      {activeTab === 1 && (
        <Card>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold">
              DETALLES DE LOS PALLETS
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">#</TableCell>
                  <TableCell>Placa</TableCell>
                  <TableCell>Detalle</TableCell>
                  <TableCell align="center">Tipo Pos</TableCell>
                  <TableCell align="center">Oversize</TableCell>
                  <TableCell align="center">DG</TableCell>
                  <TableCell>Notas</TableCell>
                  <TableCell align="center">Peso Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pallets.map((pallet, idx) => {
                  const hasDG = dangerousGoods[pallet.placa];
                  return (
                    <TableRow key={pallet.placa} hover>
                      <TableCell align="center">{idx + 1}</TableCell>
                      <TableCell>
                        <Typography fontWeight="medium">{pallet.placa}</Typography>
                      </TableCell>
                      <TableCell>
                        {pallet.guias?.map((guia, i) => (
                          <Typography key={i} variant="body2">
                            {guia}
                          </Typography>
                        ))}
                        <Typography fontWeight="medium" sx={{ mt: 0.5 }}>
                          Total: {pallet.total_piezas} piezas
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{pallet.tipoPos}</TableCell>
                      <TableCell align="center">
                        {pallet.xl && pallet.xl !== 'N/A' ? (
                          <Chip label={pallet.xl} size="small" color="warning" />
                        ) : (
                          'No'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {hasDG ? (
                          <Box>
                            <Chip
                              icon={<WarningIcon />}
                              label="DG"
                              size="small"
                              color="warning"
                              sx={{ fontWeight: 'bold' }}
                            />
                            <Box sx={{ mt: 0.5 }}>
                              {hasDG.map((dg, i) => (
                                <Typography key={i} variant="caption" display="block">
                                  UN:{dg.un} C:{dg.clase}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{pallet.notas2}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight="medium">
                          {(pallet.pesoTotalP || 0).toLocaleString()} kg
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Distribución Tab */}
      {activeTab === 2 && (
        <Card>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold">
              DISTRIBUCIÓN POR GUÍA
            </Typography>
          </Box>
          <CardContent>
            <Grid container spacing={2}>
              {distribucion.map((dist) => (
                <Grid item xs={12} md={6} key={dist.guia}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Guía: {dist.guia}
                      </Typography>
                      <Grid container spacing={1}>
                        {dist.placas.map((placa) => (
                          <Grid item xs={6} sm={4} key={placa.placa}>
                            <Chip
                              icon={<PalletIcon />}
                              label={`${placa.placa}: ${placa.piezas} pzs`}
                              size="small"
                              variant="outlined"
                            />
                          </Grid>
                        ))}
                      </Grid>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, textAlign: 'right' }}>
                        Total: {dist.total_piezas} piezas
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Posiciones Libres Tab */}
      {activeTab === 3 && (
        <Card>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold">
              POSICIONES LIBRES ({posicionesLibres.length})
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Tipo de Posición</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {posicionesLibres.length > 0 ? (
                  posicionesLibres.map((pos) => (
                    <TableRow key={pos.posicion_id} hover>
                      <TableCell>{pos.descripcion}</TableCell>
                      <TableCell>
                        <Chip label={pos.tipo_posicion} size="small" color="success" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 8 }}>
                      <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary">No hay posiciones libres</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3, '@media print': { display: 'none' } }}>
        <PDFDownloadLink
          document={<LoadSheetPDF vuelo={vuelo} pallets={pallets} dangerousGoods={dangerousGoods} />}
          fileName={`loadsheet-${vuelo.awb}.pdf`}
          style={{ textDecoration: 'none' }}
        >
          {({ loading: pdfLoading }) => (
            <Button
              variant="contained"
              color="success"
              startIcon={pdfLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              disabled={pdfLoading}
            >
              {pdfLoading ? 'Generando...' : 'Descargar PDF'}
            </Button>
          )}
        </PDFDownloadLink>
        <Button variant="contained" color="inherit" component={Link} to="/vuelos" startIcon={<BackIcon />}>
          Volver
        </Button>
      </Box>
    </Box>
  );
};

export default VueloDetail;

